import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Camera, Clock, Settings, LogOut, Leaf, Shield } from "lucide-react";

export default function Shell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";

    const linkBase =
        "flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-colors text-[11px] tracking-wide";
    const active = "text-[#1A3626] bg-[#EAF0E9]";
    const idle = "text-[#8BA092] hover:text-[#1A3626]";

    return (
        <div className="min-h-screen relative" style={{ background: "var(--nl-bg)" }}>
            <div className="max-w-2xl mx-auto min-h-screen relative pb-28 md:pb-36">
                {/* Top bar */}
                <header className="flex items-center justify-between px-6 md:px-8 pt-8 pb-4">
                    <div className="flex items-center gap-2">
                        <Leaf className="w-5 h-5" style={{ color: "#3B824E" }} />
                        <span
                            className="font-serif-editorial text-2xl leading-none"
                            style={{ color: "#1A3626" }}
                        >
                            NutriLens
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {user?.picture ? (
                            <img
                                src={user.picture}
                                alt={user.name}
                                className="w-9 h-9 rounded-full object-cover border border-[#1A3626]/10"
                                data-testid="header-avatar"
                            />
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-[#EAF0E9] flex items-center justify-center text-sm font-medium text-[#1A3626]">
                                {user?.name?.[0]?.toUpperCase() ?? "?"}
                            </div>
                        )}
                        <button
                            onClick={async () => {
                                await logout();
                                navigate("/login");
                            }}
                            data-testid="logout-btn"
                            className="p-2 rounded-full hover:bg-[#EAF0E9] text-[#4A5A4E]"
                            aria-label="Log out"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </header>

                <main className="px-6 md:px-8">{children}</main>

                {/* Bottom nav */}
                <nav className="fixed bottom-4 left-1/2 -translate-x-1/2 max-w-xl w-[92%] z-40">
                    <div className="nl-card flex items-center justify-around py-2 px-3 shadow-[0_12px_40px_rgba(26,54,38,0.12)]">
                        <NavLink
                            to="/dashboard"
                            data-testid="nav-scan"
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? active : idle}`
                            }
                        >
                            <Camera className="w-5 h-5" />
                            <span>Scan</span>
                        </NavLink>
                        <NavLink
                            to="/history"
                            data-testid="nav-history"
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? active : idle}`
                            }
                        >
                            <Clock className="w-5 h-5" />
                            <span>History</span>
                        </NavLink>
                        <NavLink
                            to="/settings"
                            data-testid="nav-settings"
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? active : idle}`
                            }
                        >
                            <Settings className="w-5 h-5" />
                            <span>Settings</span>
                        </NavLink>
                        {isAdmin && (
                            <NavLink
                                to="/admin"
                                data-testid="nav-admin"
                                className={({ isActive }) =>
                                    `${linkBase} ${isActive ? active : idle}`
                                }
                            >
                                <Shield className="w-5 h-5" />
                                <span>Admin</span>
                            </NavLink>
                        )}
                    </div>
                </nav>
            </div>
        </div>
    );
}
